/**
 * Per-deployment branding configuration.
 *
 * Branding is deployment configuration only. It controls logos, names, and
 * visual defaults for a Vercel project via NEXT_PUBLIC_BRAND. It does not select
 * the active tenant and must not be used as an authorization signal.
 *
 * Active tenant resolution is hostname-based: the server resolves the request
 * hostname through public.tenant_domain, writes session_tenant_context, and the
 * Supabase Custom Access Token Hook stamps per-session tenant/role claims.
 * Supabase RLS isolates data from those JWT claims. Stored Auth app_metadata is
 * legacy compatibility only, not canonical tenant membership.
 *
 * The Handled branding is the always-available fallback. Customer-specific
 * branding overrides are added as new tenants ship (Mercury today, others
 * later). Asset files live under `public/branding/<slug>/`.
 *
 * To swap in real assets, replace the placeholder SVGs in
 * `public/branding/<slug>/` and they'll be picked up on the next build.
 */

import { ICON_URLS } from "@/constants/icons";
import type { AccountSortColumn } from "@/components/accounts/accounts-filter-state";
import { MERCURY_DEFAULT_ACCOUNT_VISIBLE_COLUMNS } from "@/lib/mercury/account-columns";
import { HANDLED_OS_ACCOUNT_VISIBLE_COLUMNS } from "@/lib/saved-filter-views/handled-os-default-views";

export interface SplashConfig {
  /** Whether the splash page is enabled for this tenant. */
  enabled: boolean;
  /** Path under /public — icon/mark asset shown centered on the splash page. */
  iconMark: string;
  /** Welcome text shown below the icon (e.g. "WELCOME TO MERCURY OS"). */
  welcomeText: string;
  /** Giant watermark text rendered at the bottom (e.g. "MERCURY"). */
  watermarkText: string;
  /** Background color for light mode (e.g. "#f5f0ed"). */
  backgroundLight: string;
  /** Background color for dark mode (e.g. "#0e0e0e"). */
  backgroundDark: string;
}

export interface AuthPolicyConfig {
  /** Main sign-in method to emphasize on the auth screen. */
  primaryMethod: "google" | "password";
  /** Whether the email/password form is shown without extra user action. */
  showPasswordLogin: boolean;
  /** Whether users can reveal the password form when it is hidden by default. */
  allowPasswordEscapeHatch: boolean;
}

export interface BrandingConfig {
  /** Product name shown in the Chrome tab title, sign-in page, OG cards. */
  productName: string;
  /**
   * Public app address for this deployment, without protocol
   * (e.g. "app.mercury-os.com"). Used in brand-safe user-facing copy such as
   * the in-app guide, so each brand shows its own address. Optional: when
   * absent, copy falls back to a domain-free phrasing.
   */
  appUrl?: string;
  /** Short tagline used in description metadata. */
  description: string;
  /** Path under /public — favicon shown in the Chrome tab. SVG preferred. */
  favicon: string;
  /** Optional Apple touch icon (180x180 PNG ideally). */
  appleTouchIcon?: string;
  /** Path under /public — Open Graph image for social previews (1200x630 PNG/JPG ideally). */
  ogImage: string;
  /** Alt text for the OG image (accessibility + fallback). */
  ogImageAlt: string;
  /** Path under /public — full brand logo/wordmark for the login screen + app shell. */
  logo: string;
  /** Alt text for the logo. */
  logoAlt: string;
  /** Optional theme color for browser chrome (mobile address bar, PWA, etc). */
  themeColor?: string;
  /** Optional splash page configuration. When absent or disabled, unauthenticated users go directly to /login. */
  splash?: SplashConfig;
  /** Optional auth screen configuration. When absent, both Google and password are visible. */
  authPolicy?: AuthPolicyConfig;
  /** Optional quick-action modal configuration. */
  quickActions?: QuickActionConfig;
  /** Optional list of column IDs visible by default on the Accounts page. When absent, the static DEFAULT_COLUMN_VISIBILITY applies. */
  defaultAccountColumns?: string[];
  /** Optional default sort column for the Accounts page. When absent, defaults to "created_at". */
  defaultAccountSortColumn?: AccountSortColumn;
  /** Optional default sort direction for the Accounts page. When absent, defaults to "desc". */
  defaultAccountSortDirection?: "asc" | "desc";
}

export interface QuickActionTemplateConfig {
  id: string;
  label: string;
  description: string;
  /** Lucide icon name, resolved at render time via quick-actions/icons.ts */
  icon: string;
  /** Optional image URL. When present, renders an img instead of a Lucide icon. */
  iconUrl?: string;
  category?: string;
  availability?: "available" | "coming-soon";
}

export interface QuickActionConfig {
  templates: QuickActionTemplateConfig[];
  /** Heading shown above the template grid. Defaults to "What would you like to do?" */
  title?: string;
  /** Subtext below the heading. Defaults to "Choose an action to get started." */
  description?: string;
}

const BASE_QUICK_ACTION_TEMPLATES: QuickActionTemplateConfig[] = [
  {
    id: "log-product-feedback",
    label: "Log customer product feedback",
    description: "Capture feedback heard from a customer or prospect",
    icon: "ClipboardList",
    iconUrl: ICON_URLS.salesforce,
  },
  {
    id: "create-opportunity",
    label: "Create an opportunity",
    description: "Log a new sales or expansion opportunity in Salesforce",
    icon: "TrendingUp",
    iconUrl: ICON_URLS.salesforce,
  },
  {
    id: "log-call-notes",
    label: "Log call notes",
    description: "Record notes from a recent call or meeting",
    icon: "Phone",
    iconUrl: ICON_URLS.salesforce,
  },
  {
    id: "draft-email",
    label: "Send an email",
    description: "Send, test, or save a Gmail draft for a contact",
    icon: "Mail",
    iconUrl: ICON_URLS.gmail,
  },
  {
    id: "start-sub-workflow",
    label: "Start a follow-up",
    description: "Send one email or start a playbook for this contact",
    icon: "Send",
  },
  {
    id: "update-opportunity",
    label: "Update an opportunity",
    description: "Update stage, close date, or details on an existing opportunity",
    icon: "TrendingUp",
    iconUrl: ICON_URLS.salesforce,
  },
  {
    id: "request-deal-desk-approval",
    label: "Request deal desk approval",
    description: "Route a deal for approval through Slack",
    icon: "MessageSquare",
    iconUrl: ICON_URLS.slack,
    availability: "coming-soon",
  },
];

function buildQuickActionConfig(
  feedbackLabel: string,
  feedbackIconUrl?: string,
  extraTemplates: QuickActionTemplateConfig[] = [],
): QuickActionConfig {
  return {
    templates: [
      {
        id: "submit-feedback",
        label: feedbackLabel,
        description: "Share your thoughts on the platform experience",
        icon: "MessageSquare",
        ...(feedbackIconUrl ? { iconUrl: feedbackIconUrl } : {}),
      },
      ...BASE_QUICK_ACTION_TEMPLATES,
      ...extraTemplates,
    ],
  };
}

/**
 * Handled fallback branding — used whenever a customer-specific override is
 * missing or this deployment isn't tied to a specific customer.
 */
export const HANDLED_BRANDING: BrandingConfig = {
  productName: "Handled",
  description: "AI-powered operations platform for financial services teams",
  favicon: "/branding/handled/favicon.svg",
  ogImage: "/branding/handled/og-image.png",
  ogImageAlt: "Handled — AI-powered operations platform",
  logo: "/branding/handled/logo.svg",
  logoAlt: "Handled",
  quickActions: buildQuickActionConfig("Submit feedback"),
};

/**
 * Mercury OS branding — for the Mercury-specific Vercel deployment.
 * Replace the placeholder assets in `public/branding/mercury-os/` with
 * Mercury's real favicon + OG image when available.
 */
export const MERCURY_OS_BRANDING: BrandingConfig = {
  productName: "Mercury OS",
  appUrl: "app.mercury-os.com",
  description: "Mercury OS — built on the Handled platform",
  favicon: "/branding/mercury-os/favicon.svg",
  ogImage: "/branding/mercury-os/og-image.png",
  ogImageAlt: "Mercury OS — built on the Handled platform",
  logo: "/branding/mercury-os/logo.svg",
  logoAlt: "Mercury",
  splash: {
    enabled: true,
    iconMark: ICON_URLS.mercury,
    welcomeText: "WELCOME TO MERCURY OS",
    watermarkText: "MERCURY",
    backgroundLight: "#f5f0ed",
    backgroundDark: "#0e0e0e",
  },
  authPolicy: {
    primaryMethod: "google",
    showPasswordLogin: false,
    allowPasswordEscapeHatch: true,
  },
  quickActions: buildQuickActionConfig(
    "Submit Mercury OS feedback",
    ICON_URLS.mercury,
    [
      {
        id: "submit-support-request",
        label: "Submit customer support request",
        description: "Submit a support case",
        icon: "Construction",
        availability: "coming-soon",
      },
    ],
  ),
  defaultAccountColumns: [...MERCURY_DEFAULT_ACCOUNT_VISIBLE_COLUMNS],
  defaultAccountSortColumn: "name",
  defaultAccountSortDirection: "asc",
};

/**
 * Handled OS branding -- for the Handled-internal Vercel deployment.
 * Replace the placeholder assets in `public/branding/handled-os/` with
 * real Handled OS branding when available.
 */
export const HANDLED_OS_BRANDING: BrandingConfig = {
  productName: "Handled OS",
  description: "Handled OS -- AI-powered operations platform",
  favicon: "/branding/handled-os/favicon.svg",
  ogImage: "/branding/handled-os/og-image.png",
  ogImageAlt: "Handled OS -- AI-powered operations platform",
  logo: "/branding/handled-os/logo.svg",
  logoAlt: "Handled OS",
  quickActions: buildQuickActionConfig("Submit Handled OS feedback"),
  defaultAccountColumns: [...HANDLED_OS_ACCOUNT_VISIBLE_COLUMNS],
  defaultAccountSortColumn: "name",
  defaultAccountSortDirection: "asc",
};

/**
 * Registry of all known brand configs, keyed by their slug.
 * Add new entries here when onboarding a new customer deployment.
 */
const BRAND_REGISTRY: Record<string, BrandingConfig> = {
  "mercury-os": MERCURY_OS_BRANDING,
  "handled": HANDLED_BRANDING,
  "handled-os": HANDLED_OS_BRANDING,
};

/**
 * Active branding for THIS deployment.
 *
 * Read from the `NEXT_PUBLIC_BRAND` env var at build time. Defaults to
 * "handled" when unset, so the Handled-internal deployment works out of the
 * box. Set to "mercury-os" in the Mercury Vercel project's env vars.
 *
 * Falls through to `HANDLED_BRANDING` for unknown slugs — defensive default.
 */
const brandSlug = process.env.NEXT_PUBLIC_BRAND || "handled";
export const ACTIVE_BRANDING: BrandingConfig =
  BRAND_REGISTRY[brandSlug] ?? HANDLED_BRANDING;
export const ACTIVE_BRAND_SLUG = brandSlug;
